// Shared payment rail metadata and limit constants (spec §7.2, §14.1).
// Used by both the UI and the payments mock so rules live in exactly one place.
import type { BeneficiaryAccount, PaymentRail } from '@/types/phase2.types';

export const RTGS_MIN_RUPEES = 200_000;
export const IMPS_MAX_RUPEES = 500_000;
export const UPI_DAILY_CAP_RUPEES = 100_000;
export const NEW_PAYEE_CAP_RUPEES = 50_000;
export const NEW_DEVICE_CAP_RUPEES = 50_000;
export const DAILY_LIMIT_BY_PRODUCT: Record<string, number> = {
  SAVINGS: 500_000,
  SALARY: 500_000,
  CURRENT: 2_500_000,
};

export const RAIL_LABELS: Record<PaymentRail, string> = {
  UPI: 'UPI',
  NOVABANK: 'NovaBank transfer',
  NEFT: 'NEFT',
  IMPS: 'IMPS',
  RTGS: 'RTGS',
  INTERNATIONAL: 'International',
};

export const RAIL_HINTS: Record<PaymentRail, string> = {
  UPI: 'Instant, 24x7, with your UPI PIN',
  NOVABANK: 'Instant, free, between NovaBank accounts',
  NEFT: '24x7, credited within 2 hours',
  IMPS: 'Instant, 24x7, up to ₹5 lakh',
  RTGS: '₹2 lakh and above, credited within 30 minutes',
  INTERNATIONAL: 'SWIFT, usually 1 to 3 working days',
};

/** Recharge and bill-payment accounts are stored but never payable from
 *  Send money — they belong to the later bills module (§6.1). */
export const isPayableAccount = (account: BeneficiaryAccount): boolean =>
  account.kind !== 'MOBILE_TRANSFER' ||
  (account.subType !== 'MOBILE_RECHARGE' && account.subType !== 'BILL_PAYMENT');

export const isNovaIfsc = (ifsc: string): boolean => ifsc.toUpperCase().startsWith('NOVA');

export const railsForAccount = (account: BeneficiaryAccount): PaymentRail[] => {
  switch (account.kind) {
    case 'MOBILE_TRANSFER':
      return ['UPI'];
    case 'LOCAL_TRANSFER':
      return isNovaIfsc(account.ifsc) ? ['NOVABANK'] : ['IMPS', 'NEFT', 'RTGS'];
    case 'INTERNATIONAL':
      return ['INTERNATIONAL'];
  }
};

export const railFitsAmount = (rail: PaymentRail, amountRupees: number): boolean => {
  if (rail === 'RTGS') return amountRupees >= RTGS_MIN_RUPEES;
  if (rail === 'IMPS') return amountRupees <= IMPS_MAX_RUPEES;
  return true;
};

export const railDisabledReason = (rail: PaymentRail, amountRupees: number): string | null => {
  if (rail === 'RTGS' && amountRupees < RTGS_MIN_RUPEES) return 'RTGS needs ₹2,00,000 or more.';
  if (rail === 'IMPS' && amountRupees > IMPS_MAX_RUPEES) return 'IMPS allows up to ₹5,00,000 per transfer.';
  return null;
};

/** Rail suggestion (§7.2): below ₹2 lakh → IMPS, from ₹2 lakh → RTGS, unless
 *  the customer already picked a rail that still fits the amount. */
export const suggestRail = (
  rails: PaymentRail[],
  amountRupees: number,
  current?: PaymentRail
): PaymentRail | undefined => {
  if (current && rails.includes(current) && railFitsAmount(current, amountRupees)) return current;
  const preferred: PaymentRail = amountRupees >= RTGS_MIN_RUPEES ? 'RTGS' : 'IMPS';
  if (rails.includes(preferred) && railFitsAmount(preferred, amountRupees)) return preferred;
  return rails.find((r) => railFitsAmount(r, amountRupees)) ?? rails[0];
};

/** Whole-rupee Indian formatting used inside limit messages: ₹2,00,000. */
export const formatWholeRupees = (amount: number): string =>
  `₹${new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(Math.round(amount))}`;
